import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { apiErrorMessage } from '../../lib/apiErrors';
import { ConvenioSelect, ConvenioTag } from './ConvenioSelect';

// Bloco "Convênio" do perfil da internação: mostra o convênio vigente, o histórico e permite trocar com nova vigência.
export const ConvenioInternacao = ({ internacaoId, canEdit }) => {
  const { api } = useAuth();
  const [dados, setDados] = useState({ convenio: null, vigencias: [] });
  const [open, setOpen] = useState(false);
  const [novo, setNovo] = useState({ convenio_id: '', inicio: new Date().toISOString().slice(0, 10), observacao: '' });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try { setDados((await api.get(`/financeiro/convenios/internacao/${internacaoId}`)).data); } catch { /* sem convênio */ }
  }, [api, internacaoId]);
  useEffect(() => { if (internacaoId) carregar(); }, [carregar, internacaoId]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put(`/financeiro/convenios/internacao/${internacaoId}`, { ...novo, convenio_id: novo.convenio_id || null, observacao: novo.observacao || null });
      toast.success('Convênio do episódio atualizado');
      setOpen(false); carregar();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSalvando(false); }
  };

  return (
    <div data-testid="convenio-internacao">
      <Label className="text-muted-foreground">Convênio</Label>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {dados.convenio ? <ConvenioTag nome={dados.convenio.nome} cor={dados.convenio.cor} /> : <span className="text-sm text-muted-foreground" data-testid="convenio-nenhum">Não informado</span>}
        {canEdit && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setNovo({ ...novo, convenio_id: dados.convenio?.id || '' }); setOpen(true); }} data-testid="btn-alterar-convenio">
            {dados.convenio ? 'Alterar' : 'Definir'}
          </Button>
        )}
      </div>
      {dados.vigencias.length > 1 && (
        <ul className="mt-1 text-[11px] text-muted-foreground space-y-0.5" data-testid="convenio-vigencias">
          {dados.vigencias.map((v, i) => (
            <li key={i}>{v.convenio_nome}: {v.inicio}{v.fim ? ` → ${v.fim}` : ' (vigente)'}</li>
          ))}
        </ul>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="convenio-internacao-dialog">
          <DialogHeader><DialogTitle>Convênio do episódio</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <ConvenioSelect value={novo.convenio_id} onChange={(v) => setNovo({ ...novo, convenio_id: v })} testId="select-convenio-episodio" />
            <div className="space-y-1.5">
              <Label htmlFor="conv-inicio">Início da vigência</Label>
              <Input id="conv-inicio" type="date" value={novo.inicio} onChange={(e) => setNovo({ ...novo, inicio: e.target.value })} data-testid="input-convenio-inicio" />
              <p className="text-xs text-muted-foreground">A vigência anterior é encerrada nesta data e preservada no histórico.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conv-obs">Observação</Label>
              <Input id="conv-obs" value={novo.observacao} onChange={(e) => setNovo({ ...novo, observacao: e.target.value })} data-testid="input-convenio-observacao" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} data-testid="btn-salvar-convenio-episodio">{salvando ? 'Salvando…' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
